import { Edit2, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Popconfirm } from "antd";

interface FoodItem {
  id: number;
  food_item_id?: string;
  name: string;
  calories: number;
  weight: number;
  servings: number;
  image: string;
  carbohydrate_content_g?: number;
  protein_content_g?: number;
  fat_content_g?: number;
  dietary_fiber_g?: number;
  net_carbs_g?: number;
  sugar_g?: number;
  trans_fat_g?: number;
  saturated_fat_g?: number;
  GI_estimate?: number;
  GL_estimate?: number;
  serving_description?: string;
}

interface FoodItemCardProps {
  item: FoodItem;
  uploadedImage: string | null;
  isExpanded: boolean;
  onToggleExpanded: (itemId: number) => void;
  onEditItem: (item: FoodItem) => void;
  onDeleteItem: (itemId: number) => void;
}

const FoodItemCard: React.FC<FoodItemCardProps> = ({
  item,
  uploadedImage,
  isExpanded,
  onToggleExpanded,
  onEditItem,
  onDeleteItem,
}) => {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg transition-all">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-bold">{item.name}</h3>
            <span
              className="text-lg font-bold"
              style={{ color: "hsl(221, 65%, 58%)" }}
            >
              {Math.round(item.calories)} cal
            </span>
            <div className="flex items-center gap-1">
              <span>•</span>
              <span>{item.servings} Serving</span>
            </div>
          </div>
          <div className="flex gap-1">
            <button
              className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
              onClick={e => {
                e.stopPropagation();
                onEditItem(item);
              }}
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <Popconfirm
              title="Delete this food item?"
              onConfirm={e => {
                e?.stopPropagation();
                onDeleteItem(item.id);
              }}
              okText="Yes"
              cancelText="No"
            >
              <button
                className="flex h-8 w-8 items-center justify-center rounded-md text-destructive hover:bg-accent"
                onClick={e => e.stopPropagation()}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </Popconfirm>
            <button
              className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
              onClick={() => onToggleExpanded(item.id)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Nutrition Details */}
      {isExpanded && (
        <div className="space-y-3 border-t border-border bg-muted/30 px-4 pb-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Calories</span>
              <span className="font-bold text-primary">
                {Math.round(item.calories)} cal
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Protein</span>
              <span className="font-bold text-chart-1">
                {item.protein_content_g}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Total Carbs</span>
              <span className="font-bold text-chart-2">
                {item.carbohydrate_content_g}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Net Carbs</span>
              <span className="font-bold text-chart-2">
                {item.net_carbs_g ?? 0}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Fiber</span>
              <span className="font-bold text-chart-5">
                {item.dietary_fiber_g ?? 0}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Total Fat</span>
              <span className="font-bold text-chart-3">
                {item.fat_content_g}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Trans Fat</span>
              <span className="font-bold text-chart-3">
                {item.trans_fat_g ?? 0}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Saturated Fat</span>
              <span className="font-bold text-chart-3">
                {item.saturated_fat_g ?? 0}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">Sugar</span>
              <span className="font-bold text-chart-4">
                {item.sugar_g ?? 0}g
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">GI</span>
              <span className="font-bold text-orange-500">
                {item.GI_estimate?.toFixed(0) ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-sm font-semibold">GL</span>
              <span className="font-bold text-purple-500">
                {item.GL_estimate?.toFixed(0) ?? 0}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodItemCard;
